import {
  TextareaHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { cn } from "@/lib/utils";
import { InputStyles } from "@/utils/inputStyles";

const useAutoSize = (enabled: boolean = false) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = useCallback(() => {
    if (enabled && textareaRef.current) {
      // Reset height to allow shrinking
      textareaRef.current.style.height = "0px";
      // Set height to scrollHeight to fit content
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      adjustHeight();
      // Adjust on window resize
      window.addEventListener("resize", adjustHeight);
      return () => window.removeEventListener("resize", adjustHeight);
    }
  }, [enabled, adjustHeight]);

  return {
    textareaRef,
    adjustHeight,
  };
};

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoSize?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoSize = false, ...props }, ref) => {
    const { textareaRef, adjustHeight } = useAutoSize(autoSize);

    const handleRef = (element: HTMLTextAreaElement | null) => {
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
      textareaRef.current = element;
      if (element && autoSize) {
        adjustHeight();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoSize) {
        adjustHeight();
      }
      props.onChange?.(e);
    };

    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      if (autoSize) {
        adjustHeight();
      }
      props.onInput?.(e);
    };

    return (
      <textarea
        className={cn(InputStyles.Base, InputStyles.Textarea, className)}
        ref={handleRef}
        onChange={handleChange}
        onInput={handleInput}
        {...props}
      />
    );
  },
);

Textarea.displayName = "Textarea";

export { Textarea };
